import customUpdateProductPricing from "@gaiaProfessionals/Handlers/Custom/CustomUpdateProductPricingHandler";
import ProductQuantityOrdered from "@gaiaProfessionals/Overrides/Components/ProductQuantityOrdered";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductListActionsStyles;
    showAddToCartBtn?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {
    customUpdateProductPricing,
};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    addToListWrappeCheckoutDisbled?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            margin-top: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0 15px;
                        `,
                        css`
                            padding: 0 15px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-top: 10px;
            justify-content: center;
            /* background: #f3efeb; */
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 5px;
                width: 4rem;
                min-width: 4rem;
            `,
            inputSelect: css`
                height: 34px;
                text-align: center;
                font-size: 14px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            height: 40px;
                        `,
                        css`
                            height: 40px;
                        `,

                        css`
                            height: 40px;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    addToListWrappeCheckoutDisbled: {
        css: css`
            display: none;
        `,
    },
    price: {
        priceWrapper: {
            css: css``,
        },
        price: {
            priceText: {
                color: "primary",
                size: 22,
                fontFamily: "LibreBaskerville",
            },
            unitOfMeasureText: {
                color: "primary",
                size: 22,
                fontFamily: "LibreBaskerville",
            },
        },
    },
    addToListLink: {
        link: {
            css: css`
                font-size: 15px;
                font-family: "LatoBold";
            `,
        },
    },
};

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    extendedStyles,
    showAddToCartBtn,
}) => {
    const [styles] = React.useState(() => mergeToNew(actionsStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={true}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            {showAddToCartBtn && (
                <>
                    <StyledWrapper {...styles.addToCartWrapper}>
                        <ProductQuantityOrdered
                            labelOverride=""
                            extendedStyles={styles.quantityOrdered}
                            pageData="PLP"
                        />
                        <ProductAddToCartButton
                            data-test-selector={`actionsAddToCart${productContext.product.id}`}
                            extendedStyles={styles.addToCartButton}
                        />
                    </StyledWrapper>
                    {showAddToList && (
                        <StyledWrapper
                            {...(productContext.product.canAddToCart
                                ? { ...styles.addToListWrapper }
                                : { ...styles.addToListWrappeCheckoutDisbled })}
                        >
                            <ProductAddToListLink
                                data-test-selector={`actionsAddToList${productContext.product.id}`}
                                extendedStyles={styles.addToListLink}
                            />
                        </StyledWrapper>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
